/* eslint-disable */
/**
 * @flow
 *
 * Custom controller extending MousePanCameraController adding the following functionality:
 *  - Vertical bumpers
 *  - Momentum effects for mouse and touch events
 *
 * Default values for the verticalBumper is undefined and vertical bumpers are not applied by default
 * Can be adjusted via calls of
 * new CustomMousePanCameraController({frame, fov, verticalBumper})
 * in client.js
 */

import {type Quaternion, type Vec3} from 'virtual-tour-viewer/src/modules/React360Proxy';
import {MousePanCameraController} from 'virtual-tour-viewer/src/modules/React360Proxy';
import CameraUtils from '../utils/CameraUtils';
import Event from '../utils/Event.js';

const HALF_PI = Math.PI / 2;
const STOP_THRESHOLD_DEFAULT = 0.01; // The minimum velocity value that will be registered in the deceleration calculations
const FRICTION_DEFAULT = 0.92; // The rate at which velocity decays
const VELOCITY_MULTIPLIER_DEFAULT = 15;
const DECELERATION_INTERVAL_STEP = 1000 / 60; // The rate at which the deceleration step function updates the camera state (simulating 60 fps)

type TrackingPoint = {
    x: number,
    y: number,
    time: number,
};

export default class CustomMousePanCameraController extends MousePanCameraController {
    // Variables required for controlling camera velocity behavior
    _desktopFriction: number;
    _desktopStopThreshold: number;
    _desktopVelocityMultiplier: number;
    _mobileFriction: number;
    _mobileStopThreshold: number;
    _mobileVelocityMultiplier: number;

    _decelerating: Boolean;
    // _draggingMouse is true as soon as we are doing mousedown, so we should have one more property to distinguish click events
    // and the real drag event for sending Edap event info accurately
    _doDragMouse: Boolean;
    _doDragTouch: Boolean;
    _doZoom: Boolean;
    _decelerationTimerId: number;
    _trackingPoints: Array<TrackingPoint>;
    _xVelocity: number;
    _yVelocity: number;
    _handledEventEvent: Event;
    _zoomEvent: Event;
    _dragTouchEvent: Event;
    _dragEvent: Event;
    _dblClickEvent: Event;

    verticalBumper: number;

    constructor({
        frame,
        fov,
        verticalBumper,
        desktopFriction,
        desktopStopThreshold,
        desktopVelocityMultiplier,
        mobileFriction,
        mobileStopThreshold,
        mobileVelocityMultiplier,
    }) {
        super(frame, fov);

        this._desktopFriction = desktopFriction ? desktopFriction : FRICTION_DEFAULT;
        this._desktopStopThreshold = desktopStopThreshold
            ? desktopStopThreshold
            : STOP_THRESHOLD_DEFAULT;
        this._desktopVelocityMultiplier = desktopVelocityMultiplier
            ? desktopVelocityMultiplier
            : VELOCITY_MULTIPLIER_DEFAULT;
        this._mobileFriction = mobileFriction ? mobileFriction : FRICTION_DEFAULT;
        this._mobileStopThreshold = mobileStopThreshold
            ? mobileStopThreshold
            : STOP_THRESHOLD_DEFAULT;
        this._mobileVelocityMultiplier = mobileVelocityMultiplier
            ? mobileVelocityMultiplier
            : VELOCITY_MULTIPLIER_DEFAULT;

        this._decelerating = false;
        this._trackingPoints = [];
        this.verticalBumper = verticalBumper;
        this._xVelocity = 0;
        this._yVelocity = 0;
        this._handledEventEvent = new Event();
        this._zoomEvent = new Event();
        this._dragTouchEvent = new Event();
        this._dragEvent = new Event();
        this._dblClickEvent = new Event();
        this._pinchLengthStart = 0;
        this._pinchLengthEnd = 0;

        (this: any)._addTrackingPoint = this._addTrackingPoint.bind(this);
        (this: any)._startDeceleration = this._startDeceleration.bind(this);
        (this: any)._stepDeceleration = this._stepDeceleration.bind(this);
        (this: any)._endDeceleration = this._endDeceleration.bind(this);

        (this: any)._onWheel = this._onWheel.bind(this);
        this._frame.addEventListener('wheel', this._onWheel);

        (this: any)._onDoubleClick = this._onDoubleClick.bind(this);
        this._frame.addEventListener('dblclick', this._onDoubleClick, true);
    }

    subscribeHandledEvent(callback) {
        this._handledEventEvent.subscribe(callback);
    }

    unsubscribeHandledEvent(callback) {
        this._handledEventEvent.unsubscribe(callback);
    }

    doOnceDblClick(callback) {
        this._dblClickEvent.doOnce(callback);
    }

    _publishDblClick(eventArgs) {
        this._dblClickEvent.publish(this, eventArgs);
    }

    doOnceDrag(callback) {
        this._dragEvent.doOnce(callback);
    }

    _publishDrag(eventArgs) {
        this._dragEvent.publish(this, eventArgs);
    }

    doOnceDragTouch(callback) {
        this._dragTouchEvent.doOnce(callback);
    }

    _publishDragTouch(eventArgs) {
        this._dragTouchEvent.publish(this, eventArgs);
    }

    doOnceZoom(callback) {
        this._zoomEvent.doOnce(callback);
    }

    _publishZoom(eventArgs) {
        this._zoomEvent.publish(this, eventArgs);
    }

    _publishHandledEvent(eventArgs) {
        this._handledEventEvent.publish(this, eventArgs);
    }

    _onMouseDown(e: MouseEvent) {
        if (!this._enabled) {
            return;
        }
        this._publishHandledEvent(e);
        super._onMouseDown(e);
        this._endDeceleration();
        this._addTrackingPoint(this._lastMouseX, this._lastMouseY);
    }

    _onMouseMove(e: MouseEvent) {
        if (!this._draggingMouse) {
            return;
        }
        this._doDragMouse = true;
        this._publishHandledEvent(e);
        const width = this._frame.clientWidth;
        const height = this._frame.clientHeight;
        const aspect = width / height;
        const deltaX = e.clientX - this._lastMouseX;
        const deltaY = e.clientY - this._lastMouseY;
        this._lastMouseX = e.clientX;
        this._lastMouseY = e.clientY;
        this._deltaYaw += (deltaX / width) * this._verticalFov * aspect;
        this._deltaPitch += (deltaY / height) * this._verticalFov;
        this._deltaPitch = Math.max(-HALF_PI, Math.min(HALF_PI, this._deltaPitch));
        this._addTrackingPoint(e.clientX, e.clientY);
    }

    _onMouseUp() {
        if (this._doDragMouse) {
            this._publishDrag();
            this._doDragMouse = false;
        }
        super._onMouseUp();
        this._startDeceleration(
            this._desktopFriction,
            this._desktopStopThreshold,
            this._desktopVelocityMultiplier
        );
    }

    _onTouchStart(e: TouchEvent) {
        if (!this._enabled) {
            return;
        }
        this._publishHandledEvent(e);
        if (e.touches.length === 2) {
            this._doZoom = true;
            const dx = e.touches[0].pageX - e.touches[1].pageX;
            const dy = e.touches[0].pageY - e.touches[1].pageY;
            this._pinchLengthStart = Math.sqrt(dx * dx + dy * dy);
            this._pinchLengthEnd = this._pinchLengthStart;
        }
        super._onTouchStart(e);
        this._endDeceleration();
        this._addTrackingPoint(this._lastTouchX, this._lastTouchY);
    }

    _onTouchMove(e: TouchEvent) {
        if (!this._draggingTouch) {
            return;
        }
        if (e.touches.length > 1) {
            // Moving pinch-to-zoom
            const dx = e.touches[0].pageX - e.touches[1].pageX;
            const dy = e.touches[0].pageY - e.touches[1].pageY;
            this._pinchLengthEnd = Math.sqrt(dx * dx + dy * dy);
            return;
        }
        this._doDragTouch = true;
        this._publishHandledEvent(e);
        const x = e.changedTouches[0].clientX;
        const y = e.changedTouches[0].clientY;
        const width = this._frame.clientWidth;
        const height = this._frame.clientHeight;
        const aspect = width / height;
        const deltaX = x - this._lastTouchX;
        const deltaY = y - this._lastTouchY;
        this._lastTouchX = x;
        this._lastTouchY = y;
        this._deltaYaw += (deltaX / width) * this._verticalFov * aspect;
        this._deltaPitch += (deltaY / height) * this._verticalFov;
        this._deltaPitch = Math.max(-HALF_PI, Math.min(HALF_PI, this._deltaPitch));
        this._addTrackingPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    }

    _onTouchEnd(e: TouchEvent) {
        if (this._doZoom) {
            const scale = this._pinchLengthStart / this._pinchLengthEnd;
            this._publishZoom({zoomFactor: scale});
            this._doZoom = false;
        }
        if (this._doDragTouch) {
            this._publishDragTouch();
            this._doDragTouch = false;
        }
        super._onTouchEnd(e);
        this._startDeceleration(
            this._mobileFriction,
            this._mobileStopThreshold,
            this._mobileVelocityMultiplier
        );
    }

    _onWheel() {
        this._endDeceleration();
    }

    _onDoubleClick(e: MouseEvent) {
        this._publishDblClick(e);
    }

    // Track mouse/touch points within a 100ms window
    _addTrackingPoint(x: number, y: number) {
        let time = Date.now();
        while (this._trackingPoints.length > 0 && time - this._trackingPoints[0].time >= 100) {
            this._trackingPoints.shift();
        }
        this._trackingPoints.push({x, y, time});
    }

    // Calculate initial velocity based on trackingPoints and set off deceleration step intervals
    _startDeceleration(friction: number, stopThreshold: number, velocityMultiplier: number) {
        if (this._trackingPoints.length < 4) {
            return;
        }

        const firstPoint = this._trackingPoints[0];
        const lastPoint = this._trackingPoints[this._trackingPoints.length - 1];

        const xOffset = lastPoint.x - firstPoint.x;
        const yOffset = lastPoint.y - firstPoint.y;
        const timeOffset = lastPoint.time - firstPoint.time;

        this._xVelocity = (xOffset / timeOffset) * velocityMultiplier || 0;
        this._yVelocity = (yOffset / timeOffset) * velocityMultiplier || 0;

        if (Math.abs(this._xVelocity) > 1 || Math.abs(this._yVelocity) > 1) {
            this._decelerating = true;
            this._decelerationTimerId = setInterval(
                this._stepDeceleration.bind(this, friction, stopThreshold),
                DECELERATION_INTERVAL_STEP
            );
        }
    }

    // Simulate deceleration of velocity based on friction value until velocity is below the stopThreshold
    _stepDeceleration(friction: number, stopThreshold: number) {
        if (this._decelerating) {
            this._xVelocity *= friction;
            this._yVelocity *= friction;

            if (
                Math.abs(this._xVelocity) > stopThreshold ||
                Math.abs(this._yVelocity) > stopThreshold
            ) {
                const width = this._frame.clientWidth;
                const height = this._frame.clientHeight;
                const aspect = width / height;

                this._deltaYaw += (this._xVelocity / width) * this._verticalFov * aspect;
                this._deltaPitch += (this._yVelocity / height) * this._verticalFov;
                this._deltaPitch = Math.max(-HALF_PI, Math.min(HALF_PI, this._deltaPitch));
            } else {
                this._endDeceleration();
            }
        }
    }

    _endDeceleration() {
        this._decelerating = false;
        this._trackingPoints = [];
        clearInterval(this._decelerationTimerId);
    }

    fillCameraProperties(position: Vec3, rotation: Quaternion): boolean {
        if (!this._enabled) {
            return false;
        }

        if (this._deltaPitch === 0 && this._deltaYaw === 0) {
            return false;
        }

        if (!isFinite(this._deltaPitch) || !isFinite(this._deltaYaw)) {
            this._deltaPitch = 0;
            this._deltaYaw = 0;
            return false;
        }

        let newRotation = CameraUtils.rotateCamera(rotation, this._deltaYaw, this._deltaPitch);

        if (this.verticalBumper) {
            if (newRotation[3] === 0) {
                return false;
            }
            const verticalTangent = newRotation[0] / newRotation[3];
            const maximumTangentAbs = Math.tan(this.verticalBumper / 2);
            if (
                (this.verticalBumper && verticalTangent >= maximumTangentAbs) ||
                verticalTangent <= -maximumTangentAbs
            ) {
                newRotation = CameraUtils.rotateCamera(rotation, this._deltaYaw, 0);
            }
        }

        rotation[0] = newRotation[0];
        rotation[1] = newRotation[1];
        rotation[2] = newRotation[2];
        rotation[3] = newRotation[3];

        this._deltaPitch = 0;
        this._deltaYaw = 0;
        return true;
    }
}
